// The live direct-request routes (bead hf-e6-commitments-coverage-udt.1).
//
// Subscription and mutation plumbing only. Every decision about words and layout
// belongs to `RequestSurfaces.tsx`, which is why that file is testable without a
// server and this one is thin enough to read in one sitting.
//
// ── THE REVISION GOES BACK WITH THE ANSWER ─────────────────────────────────────
//
// `needRevision` is not bookkeeping. It is the revision this person was actually
// SHOWN, and sending it back is what lets the server refuse a Yes that arrived after
// an edit. Without it a fast follow-up edit would harvest the consent somebody gave to
// the first version — silent re-consent, which invariant 4 forbids. So it travels from
// the query, through the card, back to the mutation, untouched. Same discipline as
// `RecheckRoute`, for the same reason.
//
// ── AND NOTHING HERE GRANTS AN OPEN ASK ────────────────────────────────────────
//
// `myRequests` returns only open asks, and `requestsForNeed` sorts lapsed ones into
// their own list — both derived server-side from `expiresAt` on every read. The surface
// may remove a server-open row once monotonic elapsed time reaches that persisted
// deadline; it can never add or reopen one, and mutation authority remains server-side.

import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import type { requests as contracts } from "@herefor/contracts";

import { needQueries } from "../../convex/needs";
import { peopleQueries } from "../../convex/people";
import { requestMutations, requestQueries } from "../../convex/requests";
import { MatchingRoute } from "../matching";
import { requestMessageFor } from "./requestCopy";
import {
  AskAnyoneList,
  AskAudience,
  AskConfirmation,
  PendingAskList,
  type AskablePersonView,
  type AskState,
} from "./RequestSurfaces";
import { answerKey, sendKey, toAskViews, type AskView } from "./requestData";

export interface PendingAskRouteProps {
  readonly circleId: string;
  /** The circle's IANA zone, from the plan read. Windows are shown in ITS hours. */
  readonly timeZone: string;
  /**
   * Builds need routes. An opaque identifier, never anything about a person.
   *
   * Optional, and omitted deliberately by the need detail: a link back to the need
   * you are already reading is a link to nowhere. `toAskViews` renders no link at all
   * rather than a dead one.
   */
  readonly publicPlanRef?: string;
  /**
   * Show only the ask about THIS need.
   *
   * For the need detail, where a person who followed a link from their notification
   * lands and has to be able to answer without hunting for a list. Showing every
   * outstanding ask there would put a question about Thursday's delivery on a screen
   * about Tuesday's hospital run.
   *
   * Filtered in the browser rather than by a second query, because `myRequests` is
   * already the caller's OWN small list — a per-need query would be a second read of
   * the same rows, and two reads of one fact can disagree mid-flight.
   */
  readonly supportNeedId?: string;
  /** See `PendingAskListProps.hideWhenEmpty`. */
  readonly hideWhenEmpty?: boolean;
  /**
   * Passed straight through to the cards (bead hf-e6-commitments-coverage-udt.7).
   *
   * The offline bead owns the connectivity store; this route only forwards its
   * verdict, so neither feature has to import the other's internals.
   */
  readonly actionsPaused?: boolean;
}

/**
 * The asks waiting for this person, answered one at a time.
 *
 * The list is reactive both ways: an organiser who withdraws their ask removes the
 * question from this screen without the person doing anything, and an answer removes
 * its own card because the request is no longer open.
 */
export function PendingAskRoute({
  circleId,
  timeZone,
  publicPlanRef,
  supportNeedId,
  hideWhenEmpty = false,
  actionsPaused = false,
}: PendingAskRouteProps) {
  const result = useQuery(requestQueries.myRequests, { circleId });
  const respond = useMutation(requestMutations.respond);
  const [state, setState] = useState<AskState>({ status: "idle" });

  // `undefined` is Convex still loading; a rejection is a plan this person cannot
  // read. Neither is a state this surface should paint over with an empty list — an
  // empty "nobody has asked you" is a claim, and it would be a false one.
  if (result === undefined) return null;
  if (!result.ok) return null;

  const asks = toAskViews(
    supportNeedId === undefined
      ? result.value
      : result.value.filter((row) => row.supportNeedId === supportNeedId),
    { timeZone, ...(publicPlanRef === undefined ? {} : { publicPlanRef }) },
  );

  const answer = (ask: AskView, response: "accept" | "decline") => {
    // The double-tap guard. One question, one answer in flight: a second tap while the
    // first is travelling would spend the idempotency key racing itself.
    if (state.status === "sending") return;
    setState({ status: "sending", directRequestId: ask.directRequestId });

    void respond({
      circleId,
      directRequestId: ask.directRequestId,
      // The revision they were SHOWN, not the newest one. See the header.
      needRevision: ask.needRevision,
      response,
      idempotencyKey: answerKey(ask, response),
    })
      .then((outcome) => {
        // `replayed` is the SAME answer arriving twice — a retry of a request that
        // already succeeded. Treating it as anything but success would show somebody a
        // refusal for a decision the server has already recorded.
        if (outcome.status === "replayed" || outcome.value.ok) {
          // The card disappears on its own: the query re-runs because the ask is no
          // longer open. Nothing here removes it by hand.
          setState({ status: "idle" });
          return;
        }
        setState({
          status: "refused",
          directRequestId: ask.directRequestId,
          message: requestMessageFor(outcome.value.rejection).body,
        });
      })
      .catch(() => {
        // Fail-closed and VISIBLE (PLT-011): an offline answer is not queued and not
        // pretended. Somebody who believed they had accepted a hospital run when
        // nothing reached the server is the worst outcome this flow can produce.
        setState({
          status: "refused",
          directRequestId: ask.directRequestId,
          message: requestMessageFor({ kind: "not_found", entity: "directRequest" }).body,
        });
      });
  };

  return (
    <PendingAskList
      actionsPaused={actionsPaused}
      asks={asks}
      hideWhenEmpty={hideWhenEmpty}
      onAnswer={answer}
      state={state}
    />
  );
}

export interface NeedAskPanelProps {
  readonly circleId: string;
  readonly supportNeedId: string;
  readonly timeZone: string;
  /**
   * Server-decided: may this person send an ask? Never inferred here (AUT-007).
   *
   * One flag for asking AND for withdrawing, because `request.send` is the capability
   * for both: the person who may ask is the person who may un-ask.
   */
  readonly canSendRequests: boolean;
  /**
   * Server-decided: may this person read the people directory? (`people.view`.)
   *
   * A SECOND capability, not an inference from the first (bead hf-mtk). The
   * ask-anyone list reads `people.listPeople`, which is `people.view`-gated, so a
   * caller who may ask and may not read the directory is offered the list's absence
   * rather than a panel whose query throws at the boundary and takes the need detail
   * down with it (AUT-007; the shape of bead hf-e3b-circle-people-r3z.2).
   */
  readonly canViewPeople?: boolean;
  /**
   * Server-decided: may this person read recorded availability? (`availability.view`.)
   *
   * The could-help list's query is gated on it. It was rendered for everybody who
   * could reach a need, so a Membership without the capability lost the whole need
   * detail to the route boundary — the same shape as the two above, on the list that
   * has been here since ccp.2.
   */
  readonly canViewAvailability?: boolean;
  /** This person's own id, so the circle list does not offer them themselves. */
  readonly viewerPersonId?: string;
  /** Who recorded each proxy availability window, for the could-help list's provenance. */
  readonly namesByPersonId?: ReadonlyMap<string, string>;
  readonly actionsPaused?: boolean;
}

/**
 * Everything about asking, on one need, in one place (REQ-001, REQ-010, REQ-011).
 *
 * ── WHY THIS IS ONE COMPONENT AND NOT THREE ────────────────────────────────────
 *
 * Who could help, who has been asked, and the act of asking are three different
 * questions — but the middle one is the input to the other two, and splitting them
 * would mean two components each reading "who has an open ask" from their own query.
 * They would then be able to disagree: the could-help list could show Mary as
 * un-asked in the same second the audience panel said she was waiting to reply.
 *
 * So `requestsForNeed` is read ONCE here and its `awaiting` list feeds both. That is
 * the same discipline `matchingData.ts` asks for in its own words — "matching them by
 * name would be cheaper and would go wrong the first time two people shared one" — and
 * it makes an inconsistent screen unrepresentable rather than merely unlikely.
 *
 * ── AND WHY IT RENDERS THE MATCHING LIST RATHER THAN SITTING BESIDE IT ─────────
 *
 * `MatchingSurfaces` suggests and never acts; that is its whole design, and it is
 * asserted in its own tests. Handing it an `onAsk` from here keeps it that way — the
 * list reports that an organiser chose a name, and this component decides what
 * happens next. Inverting it, and letting the matching feature perform the mutation,
 * would put a write inside the one surface built to be sure it has none.
 */
export function NeedAskPanel({
  circleId,
  supportNeedId,
  timeZone,
  canSendRequests,
  canViewPeople = false,
  canViewAvailability = true,
  viewerPersonId,
  namesByPersonId,
  actionsPaused = false,
}: NeedAskPanelProps) {
  const result = useQuery(
    requestQueries.requestsForNeed,
    canSendRequests ? { circleId, supportNeedId } : "skip",
  );
  const need = useQuery(needQueries.get, { circleId, supportNeedId });
  const send = useMutation(requestMutations.send);
  const withdraw = useMutation(requestMutations.withdraw);
  const [pending, setPending] = useState<PendingAsk | null>(null);

  if ((canSendRequests && result === undefined) || need === undefined) return null;

  const audience = result?.ok ? result.value : null;
  // ONE source for "who has an open ask", shared by the list and the panel below it.
  const askedPersonIds = new Set((audience?.awaiting ?? []).map((entry) => entry.personId));

  const takeBack = (participant: contracts.RequestParticipant) => {
    if (actionsPaused) return;
    // No local state: a withdrawn ask leaves `awaiting` on the next query result, so
    // the control removes itself. A "withdrawing…" flicker on a one-field patch would
    // be more machinery than the interaction is worth.
    void withdraw({ circleId, directRequestId: participant.directRequestId }).catch(() => {
      // Fail-closed: nothing is optimistically removed, so the ask stays on screen and
      // the organiser can see it is still standing.
    });
  };

  const confirmSend = () => {
    if (pending === null || pending.busy || actionsPaused) return;
    // The revision the organiser was LOOKING AT. If the need moved while the
    // confirmation was open the server refuses, rather than the ask silently binding
    // to work nobody has seen (REQ-008).
    const needRevision = need.ok ? need.value.revision : null;
    if (needRevision === null) return;

    setPending({ ...pending, busy: true, problem: null });

    void send({
      circleId,
      supportNeedId,
      requestedPersonId: pending.personId,
      needRevision,
      idempotencyKey: sendKey(supportNeedId, pending.personId, needRevision),
    })
      .then((outcome) => {
        // `replayed` is a retry of a send that already succeeded — the ask exists, so
        // this is success. Showing a refusal would send the organiser to ask again.
        if (outcome.status === "replayed" || outcome.value.ok) {
          // The confirmation closes and the candidate is marked asked, both because
          // `requestsForNeed` re-runs. Nothing here updates a list by hand.
          setPending(null);
          return;
        }
        setPending({
          ...pending,
          busy: false,
          problem: requestMessageFor(outcome.value.rejection, pending.displayName).body,
        });
      })
      .catch(() => {
        // Fail-closed and VISIBLE (PLT-011). An organiser who believed they had asked
        // somebody, and had not, is how a need goes uncovered while looking handled.
        setPending({
          ...pending,
          busy: false,
          problem: requestMessageFor({ kind: "not_found", entity: "supportNeed" }).body,
        });
      });
  };

  return (
    <>
      {audience === null ? null : (
        <AskAudience
          actionsPaused={actionsPaused}
          audience={audience}
          {...(canSendRequests ? { onWithdraw: takeBack } : {})}
        />
      )}

      {pending === null || audience === null ? null : (
        <AskConfirmation
          busy={pending.busy || actionsPaused}
          displayName={pending.displayName}
          expiryPolicy={audience.expiryPolicy}
          onCancel={() => {
            setPending(null);
          }}
          onConfirm={confirmSend}
          problem={pending.problem}
        />
      )}

      {canViewAvailability ? (
        <MatchingRoute
          askedPersonIds={askedPersonIds}
          canAsk={canSendRequests}
          circleId={circleId}
          supportNeedId={supportNeedId}
          timeZone={timeZone}
          {...(namesByPersonId === undefined ? {} : { namesByPersonId })}
          onAsk={(candidate) => {
            setPending({
              personId: candidate.personId,
              displayName: candidate.displayName,
              busy: false,
              problem: null,
            });
          }}
        />
      ) : null}

      {/* THE SECOND ENTRY POINT (REQ-001, bead hf-mtk). The list above holds only
          people whose recorded availability contains this window; this one holds the
          circle. Same `setPending`, so the same confirmation with the same words
          follows either of them — there is one send path, not two. */}
      {canViewPeople ? (
        <AskAnyoneRoute
          askedPersonIds={askedPersonIds}
          canAsk={canSendRequests}
          circleId={circleId}
          {...(viewerPersonId === undefined ? {} : { viewerPersonId })}
          onAsk={(person) => {
            setPending({
              personId: person.personId,
              displayName: person.displayName,
              busy: false,
              problem: null,
            });
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The circle's people, as names that can be asked (bead hf-mtk).
 *
 * Reads the same directory the people screen reads, and decides nothing: who may be
 * asked at all is the server's answer, and `requests.send` refuses an unreachable
 * person with `person_not_reachable`, which the shared confirmation already renders
 * as the proxy signpost.
 *
 * WHO IS LEFT OUT, and why each is a different reason:
 *   - anybody with an open ask on this need — they are in the audience panel above,
 *     with the control to take the question back. Offering to ask them again here
 *     would be offering a refusal (`already_asked`);
 *   - the viewer — an organiser asking themselves is not a question;
 *   - anybody whose access is not active — a revoked membership keeps its history
 *     (PER-009) and is not somebody to send a new question to.
 * The supported person never appears at all: the directory is built from memberships,
 * and she has none (plan §28.4).
 */
function AskAnyoneRoute({
  circleId,
  canAsk,
  askedPersonIds,
  viewerPersonId,
  onAsk,
}: {
  readonly circleId: string;
  readonly canAsk: boolean;
  readonly askedPersonIds: ReadonlySet<string>;
  readonly viewerPersonId?: string;
  readonly onAsk: (person: AskablePersonView) => void;
}) {
  const directory = useQuery(peopleQueries.list, { circleId });

  // `undefined` is Convex still loading. The need detail owns its own loading state,
  // so this renders nothing rather than a second spinner.
  if (directory === undefined) return null;

  const people = directory.people
    .filter(
      (person) =>
        person.accessState === "active" &&
        !askedPersonIds.has(person.personId) &&
        person.personId !== viewerPersonId,
    )
    .map((person): AskablePersonView => ({
      personId: person.personId,
      displayName: person.chosenName,
      relationshipLabel: person.relationshipLabel ?? null,
    }));

  return <AskAnyoneList canAsk={canAsk} onAsk={onAsk} people={people} />;
}

/** The candidate an organiser has chosen, while the confirmation step is open. */
interface PendingAsk {
  readonly personId: string;
  readonly displayName: string;
  readonly busy: boolean;
  readonly problem: string | null;
}
